import { makeStyles } from '@material-ui/core';
import { useRouterStore } from 'mobx-state-router';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import {
  BoulderIcon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'src/common/ui/components';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { Staff_PrescriptionTask as PrescriptionTask } from 'src/generated/gql/graphql';
import { InformationRow } from 'src/myDayToday/components/InformationRow';
import { ACCEPT_PRESCRIPTION_TASK_MUTATION } from 'src/myDayToday/queries/acceptPrescriptionTask.gql';
import {
  AcceptPrescriptionTaskMutationArgs,
  AcceptPrescriptionTaskMutationResponse,
} from 'src/myDayToday/types/prescriptionTask';
import Colors from 'src/nightingale/Colors';
import { useObservability } from 'src/observability/useObservability';
import { PrescriptionTaskStatus } from 'src/prescriptionTasksQueue/PrescriptionTaskStatus';
import logger from 'src/shared/util/logger';
import { BoulderColors } from 'src/util/brand';

type PrescriptionTaskCardProps = {
  providerId: string;
  prescriptionTask: PrescriptionTask;
  mutate: () => void;
};

const PrescriptionTaskCard = (props: PrescriptionTaskCardProps) => {
  const { prescriptionTask, providerId, mutate } = props;

  const styles = useStyles();
  const routerStore = useRouterStore();
  const { apolloClient } = useContext(ApolloClientContext);
  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const sendStaffEvent = useObservability(enableObservability);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  useEffect(() => {
    sendStaffEvent({
      eventType: 'myDayToday:prescriptionTaskCardViewed',
      userId: providerId,
      details: {
        taskId: prescriptionTask.id,
        status: prescriptionTask.status,
      },
    });
  }, [sendStaffEvent, providerId, prescriptionTask.id, prescriptionTask.status]);

  const handleAcceptTask = useCallback(async () => {
    if (!apolloClient || isAccepting) {
      return;
    }

    setIsAccepting(true);

    try {
      const result = await apolloClient.mutate<
        AcceptPrescriptionTaskMutationResponse,
        AcceptPrescriptionTaskMutationArgs
      >({
        mutation: ACCEPT_PRESCRIPTION_TASK_MUTATION,
        variables: {
          taskId: prescriptionTask.id,
        },
      });

      // Check if another provider happened to accept the task before us
      const acceptedTask = result.data?.staff_acceptPrescriptionTask;
      if (acceptedTask && acceptedTask.acceptedBy !== providerId) {
        setShowConflictDialog(true);
      }

      mutate();
    } catch (error) {
      logger.error('Error accepting prescription task:', error, {
        taskId: prescriptionTask.id,
        providerId,
      });
    } finally {
      setIsAccepting(false);
    }
  }, [apolloClient, isAccepting, prescriptionTask.id, providerId, mutate]);

  const handleViewTask = useCallback(() => {
    routerStore.goTo('showEvent', { params: { id: prescriptionTask.eventId } });
  }, [routerStore, prescriptionTask.eventId]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.card} data-testid="task-card">
          <div className="body">
            <div className="details">
              <div className={styles.header}>
                <div className={styles.badge}>
                  <BoulderIcon icon="localPharmacyOutlined" size={20} />
                  <span className={styles.badgeText}>PRESCRIPTION TASK</span>
                </div>
              </div>
              <div className={styles.patientName}>
                <div>
                  {prescriptionTask.patient.preferredName || prescriptionTask.patient.firstName}{' '}
                  {prescriptionTask.patient.lastName}
                </div>
              </div>
              <div className={styles.noteAndButtonRow}>
                <InformationRow
                  icon={<BoulderIcon icon="recordVoiceOverOutlined" size={18} />}
                  label="Note for clinician"
                  value={prescriptionTask.rnNote || 'No note provided'}
                />
                {prescriptionTask.status === PrescriptionTaskStatus.Pending && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<BoulderIcon icon="check" size={18} />}
                    data-testid="accept-prescription-task-button"
                    disabled={isAccepting}
                    onClick={handleAcceptTask}
                  >
                    ACCEPT
                  </Button>
                )}
                {prescriptionTask.status === PrescriptionTaskStatus.Accepted && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<BoulderIcon icon="check" size={18} />}
                    data-testid="view-prescription-task-button"
                    onClick={handleViewTask}
                  >
                    VIEW
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showConflictDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Task Already Accepted</DialogTitle>
        <DialogContent>
          This prescription task has already been accepted by another provider.
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setShowConflictDialog(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  noteAndButtonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  container: {
    boxSizing: 'border-box',
    border: '1px solid',
    borderColor: BoulderColors.Gray2,
    background: Colors.White,
    display: 'flex',
    width: '675px',
    padding: '24px 36px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  card: {
    background: Colors.White,
    padding: '16px',
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    width: '100%',
    border: `1px solid ${BoulderColors.Gray3}`,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: '12px',
  },
  badge: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: BoulderColors.Gray2,
    borderRadius: '12px',
  },
  badgeText: {
    color: BoulderColors.Blue6,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '100%',
    letterSpacing: '0.201px',
  },
  patientName: {
    alignSelf: 'stretch',
    color: Colors.Blue4,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '145%',
    letterSpacing: '0.201px',
  },
});

export default PrescriptionTaskCard;
